import React, { useState, forwardRef, useImperativeHandle } from "react";
import { useDispatch } from "react-redux";

import { resetPasswordConfirm } from "src/redux/slices/Auth";

import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
// material
import {
    Stack,
    TextField,
    IconButton,
    InputAdornment,
    Typography,
} from "@mui/material";

import Loader from "src/components/loader/Loader";
import ContentCard from "../Profile/ContentCard";
import { useNavigate } from "react-router";
import { useTranslation } from 'react-i18next';

// ----------------------------------------------------------------------

const PasswordResetConfirmForm = forwardRef(({ uid, token }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    const LoginSchema = Yup.object().shape({
        new_password: Yup.string().required(t('FormCommon.required')),
        re_new_password: Yup.string()
            .required(t('FormCommon.required'))
            .oneOf([Yup.ref("new_password"), null], t('PasswordResetConfirmForm.re_new_password.invalid')),
    });

    const formik = useFormik({
        initialValues: {
            new_password: "",
            re_new_password: "",
        },
        validationSchema: LoginSchema,
        onSubmit: (values, actions) => {
            setLoading(true);
            const { new_password, re_new_password } = values;
            dispatch(
                resetPasswordConfirm({
                    t,
                    uid,
                    token,
                    new_password,
                    re_new_password,
                })
            )
                .unwrap()
                .then(() => {
                    setLoading(false);
                    actions.setSubmitting(false);
                    actions.resetForm();
                    navigate("/login");
                })
                .catch(() => {
                    setLoading(false);
                    actions.setSubmitting(false);
                });
        },
    });

    const {
        errors,
        touched,
        values,
        isSubmitting,
        handleSubmit,
        getFieldProps,
    } = formik;

    useImperativeHandle(ref, () => ({
        submitForm() {
            formik.handleSubmit();
        },
    }));

    return (
        <ContentCard>
            <Typography variant="h4" gutterBottom>
                {t('PasswordResetConfirmForm.title')}
            </Typography>
            <Typography sx={{ color: "text.secondary" }} mb={1}>
                {t('PasswordResetConfirmForm.description')}
            </Typography>

            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    {loading ? (
                        <Loader />
                    ) : (
                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            spacing={2}
                            mb={2}
                        >
                            <TextField
                                fullWidth
                                autoComplete="new_password"
                                type={showPassword ? "text" : "password"}
                                label={t('PasswordResetConfirmForm.new_password.placeholder')}
                                {...getFieldProps("new_password")}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    setShowPassword(
                                                        (prev) => !prev
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon={
                                                        showPassword
                                                            ? eyeFill
                                                            : eyeOffFill
                                                    }
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                error={Boolean(
                                    touched.new_password && errors.new_password
                                )}
                                helperText={
                                    touched.new_password && errors.new_password
                                }
                            />
                            <TextField
                                fullWidth
                                autoComplete="re_new_password"
                                type={showPassword ? "text" : "password"}
                                label={t('PasswordResetConfirmForm.re_new_password.placeholder')}
                                {...getFieldProps("re_new_password")}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    setShowPassword(
                                                        (prev) => !prev
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon={
                                                        showPassword
                                                            ? eyeFill
                                                            : eyeOffFill
                                                    }
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                error={Boolean(
                                    touched.re_new_password &&
                                        errors.re_new_password
                                )}
                                helperText={
                                    touched.re_new_password &&
                                    errors.re_new_password
                                }
                            />
                        </Stack>
                    )}
                </Form>
            </FormikProvider>
        </ContentCard>
    );
});

export default PasswordResetConfirmForm;
